import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";

import { useAdditionalFields } from "../hooks/addition-fields";
import { useGetApartments } from "../queries/get-apartments";
import { useGetOrganizations } from "../queries/organization";
import { useGetPersons } from "../queries/person";
import { formatApartmentLabel } from "../services/apartment";
import { formatOrganizationLabel } from "../services/organization";
import { formatPersonLabel } from "../services/person";
import { useLocale } from "../stores/locale";
import { AdditionalFields } from "./additional-fields";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Input } from "./input";
import { ResidencyNameInput } from "./residency-name-input";
import { Select } from "./select";

export type HoldingFormValues = {
  apartmentId: string;
  fromDate: string;
  toDate: string;
  owners: ({
    primary: boolean;
    share: number;
  } & (
    | {
        personId: string;
      }
    | {
        organizationId: string;
      }
  ))[];
  useCustomName: boolean;
  customName: string;
  additionalFields?: Record<string, string>;
};

export function HoldingForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  disabled = false,
  hasError = false,
}: {
  onCancel: () => void;
  onChange: (values: HoldingFormValues) => void;
  onSubmit: () => void;
  values: HoldingFormValues;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const t = useLocale((state) => state.locale);
  const { data: additionalFields } = useAdditionalFields("holding");
  const { data: apartments } = useGetApartments();

  const ownerShareSum = useMemo(() => {
    return values.owners.reduce((sum, owner) => sum + owner.share, 0);
  }, [values.owners]);

  const isValid = useMemo(() => {
    return (
      values.apartmentId !== "" &&
      values.fromDate.trim() !== "" &&
      values.owners.length > 0 &&
      values.owners.every(
        (owner) =>
          ("personId" in owner && owner.personId !== "") ||
          ("organizationId" in owner && owner.organizationId !== ""),
      ) &&
      values.owners.some((owner) => owner.primary) &&
      ownerShareSum === 100 &&
      additionalFields.every(
        (field) =>
          !field.required ||
          values.additionalFields?.[field.name]?.trim() !== "",
      )
    );
  }, [values, additionalFields, ownerShareSum]);
  const isDisabled = useMemo(() => {
    return disabled || loading;
  }, [disabled, loading]);

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <Select
          className="col-span-2"
          label={t.apartment.apartment}
          options={
            apartments?.map((apartment) => ({
              label: formatApartmentLabel(apartment),
              value: apartment.id,
            })) ?? []
          }
          placeholder={t.select.placeholder}
          value={values.apartmentId}
          onChange={(value) => onChange({ ...values, apartmentId: value })}
        />
        <Input
          required
          disabled={isDisabled}
          label={t.holding.fromDate}
          type="date"
          value={values.fromDate}
          onChange={(e) => onChange({ ...values, fromDate: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={t.holding.toDate}
          type="date"
          value={values.toDate}
          onChange={(e) => onChange({ ...values, toDate: e.target.value })}
        />
        <div className="col-span-2 gap-2">
          {values.owners.map((owner, index) => (
            <OwnerSelect
              key={index}
              excludeOwners={values.owners
                .filter((_, i) => i !== index)
                .map((o) =>
                  "personId" in o
                    ? { personId: o.personId }
                    : { organizationId: o.organizationId },
                )}
              isRemoveDisabled={values.owners.length === 1}
              owner={owner}
              onChange={(owner) =>
                onChange({
                  ...values,
                  owners: values.owners.map((o, i) =>
                    i === index ? owner : o,
                  ),
                })
              }
              onPrimaryChange={(changes) =>
                // Make sure only one owner is primary
                onChange({
                  ...values,
                  owners: values.owners.map((o) => ({
                    ...o,
                    primary:
                      changes.primary &&
                      (("personId" in changes &&
                        "personId" in o &&
                        changes.personId === o.personId) ||
                        ("organizationId" in changes &&
                          "organizationId" in o &&
                          changes.organizationId === o.organizationId)),
                  })),
                })
              }
              onRemove={() =>
                onChange({
                  ...values,
                  owners: values.owners.filter((_, i) => i !== index),
                })
              }
            />
          ))}
        </div>
        <div className="flex col-span-2 gap-2">
          <Button
            onClick={() =>
              onChange({
                ...values,
                owners: [
                  ...values.owners.map((o, i, arr) => {
                    if (i === arr.length - 1) {
                      return {
                        ...o,
                        share: Math.ceil(o.share / 2),
                      };
                    }
                    return o;
                  }),
                  {
                    personId: "",
                    primary: false,
                    share:
                      values.owners.length >= 1
                        ? Math.floor(
                            (values.owners[values.owners.length - 1]?.share ??
                              0) / 2,
                          )
                        : 100,
                  },
                ],
              })
            }
          >
            Add owner
          </Button>
        </div>
        <ResidencyNameInput
          className="col-span-2"
          customName={values.customName}
          disabled={isDisabled}
          residents={values.owners}
          useCustomName={values.useCustomName}
          onChange={(changes) => onChange({ ...values, ...changes })}
        />
        <AdditionalFields
          additionalFields={additionalFields}
          disabled={isDisabled}
          values={values.additionalFields ?? {}}
          onChange={(additionalFieldValues) =>
            onChange({
              ...values,
              additionalFields: {
                ...additionalFieldValues,
              },
            })
          }
        />
      </div>
      <div className="flex gap-2 justify-end">
        {hasError && (
          <p className="text-red-500 flex items-center gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {t.holding.error}
          </p>
        )}
        <Button disabled={disabled} severity="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={disabled || !isValid} loading={loading} type="submit">
          Save
        </Button>
      </div>
    </form>
  );
}

function OwnerSelect({
  owner,
  excludeOwners = [],
  onChange,
  onRemove,
  onPrimaryChange,
  isRemoveDisabled = false,
}: {
  owner: HoldingFormValues["owners"][0];
  excludeOwners?: ({ personId: string } | { organizationId: string })[];
  onChange: (owner: HoldingFormValues["owners"][0]) => void;
  onRemove: () => void;
  onPrimaryChange: (
    changes: ({ personId: string } | { organizationId: string }) & {
      primary: boolean;
    },
  ) => void;
  isRemoveDisabled?: boolean;
}) {
  const { data: persons } = useGetPersons();
  const { data: organizations } = useGetOrganizations();
  const t = useLocale((state) => state.locale);

  const isDisabled = useMemo(() => {
    return (
      ("personId" in owner && owner.personId === "") ||
      ("organizationId" in owner && owner.organizationId === "")
    );
  }, [owner]);
  return (
    <div className="grid grid-cols-5 gap-2">
      <Select
        label="Type"
        options={[
          { label: "Person", value: "person" },
          { label: "Organization", value: "organization" },
        ]}
        value={"personId" in owner ? "person" : "organization"}
        onChange={(value) => {
          if (value === "person") {
            onChange({
              personId: "",
              primary: owner.primary,
              share: owner.share,
            });
          } else {
            onChange({
              organizationId: "",
              primary: owner.primary,
              share: owner.share,
            });
          }
        }}
      />
      {"personId" in owner ? (
        <Select
          label={t.person.person}
          options={
            persons
              ?.filter(
                (person) =>
                  !excludeOwners.some(
                    (o) => "personId" in o && o.personId === person.id,
                  ),
              )
              .map((person) => ({
                label: formatPersonLabel(person),
                value: person.id,
              })) ?? []
          }
          placeholder={t.select.placeholder}
          value={owner.personId}
          onChange={(value) => onChange({ ...owner, personId: value })}
        />
      ) : "organizationId" in owner ? (
        <Select
          label={t.organization.organization}
          options={
            organizations
              ?.filter(
                (organization) =>
                  !excludeOwners.some(
                    (o) =>
                      "organizationId" in o &&
                      o.organizationId === organization.id,
                  ),
              )
              ?.map((organization) => ({
                label: formatOrganizationLabel(organization),
                value: organization.id,
              })) ?? []
          }
          placeholder={t.select.placeholder}
          value={owner.organizationId}
          onChange={(value) => onChange({ ...owner, organizationId: value })}
        />
      ) : null}
      <Input
        disabled={isDisabled}
        label={t.holding.owner.share}
        suffix="%"
        type="number"
        value={owner.share.toString()}
        onChange={(e) => onChange({ ...owner, share: Number(e.target.value) })}
      />
      <Checkbox
        checked={owner.primary}
        disabled={isDisabled}
        label={t.holding.owner.primary}
        onClick={() => {
          onPrimaryChange({ ...owner, primary: !owner.primary });
        }}
      />
      <Button disabled={isRemoveDisabled} severity="danger" onClick={onRemove}>
        Remove
      </Button>
    </div>
  );
}
